/* import __COLOCATED_TEMPLATE__ from './configure-plan.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  P5_CORE_PLAN_UPGRADE_FEATURES,
  PLAN_DATA,
  PRICING_5_X_CORE_ESSENTIAL_ID,
} from 'embercom/lib/billing';
import type Plan from 'embercom/models/plan';
import type QuoteService from 'embercom/services/quote-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';

interface Args {
  corePlans: Array<Plan>;
  yearlyBillingPeriodSelected: boolean;
  selectedPlanId: string;
  seatNumber: number;
  minimumSeatNumber: number;
  selectedProactiveAddon: boolean;
  selectedCopilotAddon: boolean;
  selectedCopilotAdmins: Array<string>;
  copilotInitialised: boolean;
  hasSelectedAiInsightsAddon: boolean;
  fullSeatPrice: number;
  discountedSeatPrice: number;
  fullProactiveAddonPrice: number;
  perSeatCopilotAddonPrice: number;
  discountedCopilotSeatPrice: number;
  fullAiInsightsAddonPrice?: number;
  proactiveSupportBaseUsage: string;
  loading: boolean;
  updateSeatNumber: (seatNumber: number) => void;
  updateSelectedPlan: (plan: Plan) => void;
  setSelectedProactiveAddon: (selectedProactiveAddon: boolean) => void;
  setCopilotAsInitialised: (selectedCopilotAddon: boolean) => void;
  setHasSelectedAiInsightsAddon: (hasSelectedAiInsightsAddon: boolean) => void;
  showProactiveSupportProArticle: () => void;
  isEarlyStageProgression: boolean;
  openCopilotModalOnLoad?: boolean;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class ConfigurePlan extends Component<Signature> {
  @service declare intl: any;
  @service declare quoteService: QuoteService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare aiAgentSetupService: AiAgentSetupService;

  @tracked isCopilotSelectorModalOpen = this.args.openCopilotModalOnLoad || false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.aiAgentSetupService.reloadWorkflowsCounts();
  }

  get seatInputLabelKey() {
    return 'signup.teams.pricing5.annual-plans.configure-plan.seat-input-label';
  }

  get selectedPlan() {
    return this.args.corePlans.find((plan) => plan.id === this.args.selectedPlanId);
  }

  get planFeatureKeys(): any {
    return P5_CORE_PLAN_UPGRADE_FEATURES[this.args.selectedPlanId];
  }

  get proactiveSupportPlusArticleId() {
    return 9061648; // https://www.intercom.com/help/articles/9061648
  }

  get proactiveSupportPlusFeatures() {
    return [
      [
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.product-tours.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.product-tours.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.surveys.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.surveys.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.posts.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.posts.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.mobile-carousel.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.mobile-carousel.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.push-notifications.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.push-notifications.tooltip`,
          ),
        },
      ],
      [
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.checklists.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.checklists.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.news-items.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.news-items.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.webhooks.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.webhooks.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.series-campaign-builder.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.series-campaign-builder.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.a-b-testing.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.proactive-support.features.a-b-testing.tooltip`,
          ),
        },
      ],
    ];
  }

  get aiInsightsSubcopyList() {
    return [
      {
        text: this.intl.t(`signup.teams.pricing5.annual-plans.upgrades.ai-insights.subcopy`),
      },
    ];
  }

  get aiInsightsHelpdeskArticleId() {
    return 13003501; // https://www.intercom.com/help/articles/13003501
  }

  get aiInsightsFeatures() {
    return [
      [
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.cx-score.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.cx-score.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.optimize.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.optimize.tooltip`,
          ),
        },
      ],
      [
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.topics-explorer.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.topics-explorer.tooltip`,
          ),
        },
        {
          text: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.trending-topics.title`,
          ),
          tooltip: this.intl.t(
            `signup.teams.pricing5.annual-plans.upgrades.ai-insights.features.trending-topics.tooltip`,
          ),
        },
      ],
    ];
  }

  get proactiveSupportPlusSubcopyList() {
    return [
      {
        text: this.intl.t(
          `signup.teams.pricing5.annual-plans.upgrades.proactive-support.subcopy-1`,
          { baseUsage: this.args.proactiveSupportBaseUsage },
        ),
      },
      {
        text: this.intl.t(
          `signup.teams.pricing5.annual-plans.upgrades.proactive-support.subcopy-2`,
        ),
      },
    ];
  }

  get copilotArticleId() {
    return 9121374; // https://www.intercom.com/help/articles/9121374
  }

  get seatNumber() {
    return this.args.seatNumber;
  }

  set seatNumber(value: number) {
    let seatNumber: number;
    if (value <= this.args.minimumSeatNumber) {
      seatNumber = this.args.minimumSeatNumber;
    } else if (value > 100) {
      seatNumber = 100;
    } else {
      seatNumber = value;
    }
    this.args.updateSeatNumber(seatNumber);
  }

  get notLowestTierPlan() {
    let plan = this.args.corePlans.find((plan) => plan.id === this.args.selectedPlanId) as Plan;
    return plan.tierId > 1;
  }

  get isPlanWithLiteSeatsSelected() {
    return (PLAN_DATA[this.args.selectedPlanId].liteSeatLimit ?? 0) > 0;
  }

  get planList() {
    return this.args.corePlans.map((plan: Plan) => {
      return {
        text: plan.name,
        description: this.intl.t(PLAN_DATA[plan.id].checkoutDescriptionTranslationKey),
        value: plan.id,
      };
    });
  }

  get featuresLeftCol() {
    return this.planFeatureKeys.left;
  }

  get featuresRightCol() {
    return this.planFeatureKeys.right;
  }

  get seatPrice() {
    return this.args.yearlyBillingPeriodSelected
      ? this.args.discountedSeatPrice
      : this.args.fullSeatPrice;
  }

  get showCopilotAddonCard() {
    return this.args.isEarlyStageProgression
      ? this.args.selectedCopilotAdmins.length > 0
      : this.appService.app.copilotProduct;
  }

  get showAiInsightsAddonCard() {
    return this.appService.app.canSeeAiInsightsAddon;
  }

  get shouldSeatSelectorBeDisabled() {
    return this.args.isEarlyStageProgression;
  }

  get liteSeatNumber() {
    return this.appService.app.humanAdminsWithLiteSeat.length;
  }

  get shouldShowLiteSeatToFullSeatWarning() {
    return (
      this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID &&
      this.appService.app.humanAdminsWithLiteSeat.length > 0
    );
  }

  get shouldShowPauseWorkflowsWarning() {
    return (
      this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID &&
      (this.aiAgentSetupService.totalLiveWorkflowsCount > 0 ||
        this.aiAgentSetupService.totalPausedWorkflowsCount > 0)
    );
  }

  @action updateSelectedPlan(planId: string) {
    // this will not be undefined but typescript isn't happy
    let plan = this.args.corePlans.find((plan) => plan.id === planId) as Plan;
    this.args.updateSelectedPlan(plan);
  }

  @action toggleCopilotSelectorModal() {
    this.isCopilotSelectorModalOpen = !this.isCopilotSelectorModalOpen;
  }

  @action confirmCopilotSelection() {
    this.toggleCopilotSelectorModal();
    this.args.setCopilotAsInitialised(true);
  }

  @action toggleEsProgressionCopilotSelection() {
    this.args.setCopilotAsInitialised(!this.args.selectedCopilotAddon);
  }

  @action toggleAddonSelectedState() {
    if (this.args.isEarlyStageProgression) {
      this.toggleEsProgressionCopilotSelection();
    } else {
      this.toggleCopilotSelectorModal();
    }
  }

  @action toggleAiInsightsAddon() {
    this.args.setHasSelectedAiInsightsAddon(!this.args.hasSelectedAiInsightsAddon);
  }

  @action showSeatsArticle(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'learn_more_about_seats_article',
      place: 'p5-checkout-convert-to-paid',
    });
    window.Intercom('showArticle', 8205716); // https://www.intercom.com/help/en/articles/8205716-seats
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::ConfigurePlan': typeof ConfigurePlan;
  }
}
